import { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  TDocumentFeedbackItem,
  TDocsFeedbackType,
} from 'lendingApi/lendingApi.types';

type TFeedbackGroup = {
  types: TDocsFeedbackType[];
  message: ReactNode;
};

const getFeedbackGroups = (): TFeedbackGroup[] => [
  {
    types: ['ILLEGIBLE'],
    message: (
      <Trans>
        This bank statement is <strong>illegible</strong>. Please upload a new
        document in PDF format that is of sufficient quality.
      </Trans>
    ),
  },
  {
    types: ['INVALID_DOCUMENT_FORMAT'],
    message: (
      <Trans>
        This bank statement is <strong>not</strong> available in{' '}
        <strong>PDF format</strong>. Please upload a new document that is in its
        original PDF format, directly from your online banking.
      </Trans>
    ),
  },
  {
    types: ['PASSWORD_NOT_AVAILABLE'],
    message: (
      <Trans>
        This bank statement is <strong>password protected</strong>. Please
        upload a new, unprotected PDF document or send us the password via
        email.
      </Trans>
    ),
  },
  {
    types: ['REFERENCES_MISSING'],
    message: (
      <Trans>
        This bank statement contains{' '}
        <strong>incomplete transaction references</strong>. Please upload a new
        document in PDF format that includes complete and detailed transaction
        references. Further information on the required references can be found{' '}
        <a
          href={t`https://www.iwoca.co.uk/references-missing-link`}
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        .
      </Trans>
    ),
  },
  {
    types: ['BALANCE_FIGURES_MISSING'],
    message: (
      <Trans>
        This statement does not include the <strong>balance</strong>. Please
        upload a new document in PDF format showing the complete account balance
        at the beginning and end of the transactions.
      </Trans>
    ),
  },
  {
    // BALANCE_MISMATCH and MISSING_TRANSACTIONS share the same user-facing message.
    types: ['MISSING_TRANSACTIONS', 'BALANCE_MISMATCH'],
    message: (
      <Trans>
        Please upload a new bank statement that has{' '}
        <strong>complete transactions</strong> and is in its original PDF
        format, directly from online banking.
      </Trans>
    ),
  },
  {
    types: ['MISSING_APPLICANT_NAME_AND_COMPANY_NAME'],
    message: (
      <Trans>
        This bank statement does <strong>not</strong> contain your client's{' '}
        <strong>full name</strong> and <strong>company name</strong>. Please
        upload a new document in PDF format that includes this information.
      </Trans>
    ),
  },
  {
    types: ['MISSING_SUMMARY_PAGE'],
    message: (
      <Trans>
        This bank statement does not contain a <strong>summary page</strong>.
        Please upload a new document in PDF format that includes the first page
        with the bank's name and logo, the full IBAN, your client's full name,
        and the company name.
      </Trans>
    ),
  },
  {
    types: ['MISSING_LOGO_AND_IBAN'],
    message: (
      <Trans>
        This bank statement does not contain the <strong>bank logo</strong>{' '}
        and/or <strong>IBAN</strong>. Please upload a new document in PDF format
        that contains this information.
      </Trans>
    ),
  },
  {
    types: ['SCREENSHOT'],
    message: (
      <Trans>
        This bank statement is a <strong>screenshot</strong>. Please upload a
        new document in PDF format that was exported directly from your online
        banking.
      </Trans>
    ),
  },
  {
    types: ['ACTIVE_LINKS'],
    message: (
      <Trans>
        This bank statement contains <strong>active links</strong> in the
        transaction lines. Please upload a new document without active links in
        PDF format.
      </Trans>
    ),
  },
  {
    types: ['INCORRECT_ORDER'],
    message: (
      <Trans>
        This bank statement shows transactions that are{' '}
        <strong>not in chronological order</strong>. Please upload a new
        document in PDF format that contains the transactions in chronological
        order.
      </Trans>
    ),
  },
];

export const getFeedbackMessages = (
  docsFeedback: TDocumentFeedbackItem[],
): ReactNode[] =>
  getFeedbackGroups()
    .filter((group) =>
      group.types.some((type) =>
        docsFeedback.some((f) => f.feedback_type === type),
      ),
    )
    .map((group) => group.message);
